import React from "react";
import "./invitationform.scss";
// import BreadCrumsHeader from "../../common/BreadCrums"; // Removed as per request
import Input from "../../common/FormInputs";
// import FormLayout from "../../common/FormLayout"; // Removed to customize layout
import { useFormik } from "formik";
import * as Yup from "yup";
import { sendAdminInvite } from "../../../services/onboardingService";
import { emailVerification } from "../../../services/authService";
import { getMerchants } from "../../../services/merchantService";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../../common/Loader";
import { toast } from "react-toastify";
import axiosInstance from "../../../utils/axios";
import { ReactComponent as InfoIcon } from "../../../Assets/Icons/info.svg";
import { ReactComponent as UploadIcon } from "../../../Assets/Icons/UploadCSVIcon.svg";
import { ReactComponent as CsvIcon } from "../../../Assets/Icons/csvIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../Assets/Icons/downloadIcon.svg";
import { Tooltip } from "@mui/material";
import { X, Eye, Trash2, Check, Pencil } from "lucide-react";
import DetailsModal from "../../common/DetailsModal";

const InvitaionFormPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const [csvFile, setCsvFile] = React.useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState(null);
  const [editedEmail, setEditedEmail] = React.useState("");
  const [isVerifying, setIsVerifying] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [emailStatus, setEmailStatus] = React.useState({}); // { email: "valid" | "already_registered" | "invalid" }

  const checkMerchantRegistration = React.useCallback(async (email) => {
    if (!email) return "valid";
    const cleanE = email.trim().toLowerCase();
    try {
      // 1. Check Merchant Collection
      const mRes = await axiosInstance.get(`merchant?search=${cleanE}&limit=1000`);
      const merchants = mRes?.data?.data || [];
      const merchant = merchants.find(m =>
        (m?.user?.email?.toLowerCase() === cleanE) ||
        (m?.email?.toLowerCase() === cleanE) ||
        (m?.businessEmail?.toLowerCase() === cleanE) ||
        (m?.business_email?.toLowerCase() === cleanE) ||
        (m?.merchantEmail?.toLowerCase() === cleanE)
      );

      if (merchant) {
        if (merchant?.status?.toLowerCase() === "approved") {
          return "registered";
        }
        return "in-process";
      }

      // 2. Check Invitation Collection
      // We check if an invitation exists for this email
      const iRes = await axiosInstance.get(`merchant/invitations?limit=1000`);
      const invitations = iRes?.data?.data || [];
      const invitation = invitations.find(i => i?.email?.toLowerCase() === cleanE);

      if (invitation) {
        return "already_invited";
      }

      return "valid";
    } catch (_) {
      return "valid";
    }
  }, []);

  const invitationSchema = React.useMemo(() => Yup.object().shape({
    email: Yup.string()
      .required("Email is required")
      .test("is-valid-email-list", "One or more email addresses are invalid", (value) => {
        if (!value) return true;
        const emails = value.split(",").map((e) => e.trim()).filter(Boolean);
        return emails.every((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      })
      .test("is-not-registered", function (value) {
        if (!value) return true;
        const emails = value.split(",").map((e) => e.trim()).filter(Boolean);
        const registered = emails.filter(e => emailStatus[e] === "registered");
        if (registered.length > 0) {
          return this.createError({
            message: registered.length === 1
              ? `The email ${registered[0]} is already a Registered Merchant.`
              : `The following emails are already Registered Merchants: ${registered.join(", ")}`
          });
        }
        return true;
      }),
  }), [emailStatus]);

  const formik = useFormik({
    initialValues: {
      email: "",
    },
    enableReinitialize: true,
    validationSchema: invitationSchema,
    onSubmit: async (values) => {
      try {
        const payload = {
          emails: values?.email,
        };

        const invitePromise = await dispatch(sendAdminInvite(payload)).unwrap();

        if (invitePromise?.success === true) {
          toast.success("Merchant invitation sent successfully!", {
            style: {
              background: "#E6F7FB",
              color: "#007EA7",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              padding: "12px 16px",
            },
            progressStyle: {
              background: "#007EA7",
            },
            icon: "✅",
          });
          navigate("/invitation-list");
        } else {
          toast.error(invitePromise?.message || "Failed to send invitation", {
            style: {
              background: "#FEF2F2",
              color: "#DC2626",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
              padding: "12px 16px",
            },
            progressStyle: {
              background: "#DC2626",
            },
            icon: "❌",
          });
        }
      } catch (error) {
        toast.error("An unexpected error occurred. Please try again.");
      }
    },
  });

  const { email } = formik.values;
  const { handleChange, handleBlur, handleSubmit, errors, touched } = formik;

  const handleSampleDownload = () => {
    const csvContent = "Email\nmerchant1@example.com\nmerchant2@example.com";
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "sample_merchant_emails.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleClearCSV = () => {
    setCsvFile(null);
    formik.setFieldValue("email", "");
    setInputValue("");
    const fileInput = document.getElementById("csvFileInput");
    if (fileInput) fileInput.value = "";
  };

  const handleManualEmailAdd = async (val) => {
    const cleanEmail = val.trim().replace(/,/g, "");
    if (!cleanEmail) return;

    // Basic format check before adding as tag
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      formik.setFieldError("email", "Invalid email format");
      return;
    }

    const currentEmails = formik.values.email ? formik.values.email.split(",").map(i => i.trim()).filter(Boolean) : [];

    if (currentEmails.includes(cleanEmail)) {
      formik.setFieldError("email", "Email already added");
      return;
    }

    const newEmails = [...currentEmails, cleanEmail];
    formik.setFieldValue("email", newEmails.join(", "), true);
    setInputValue("");

    const status = await checkMerchantRegistration(cleanEmail);
    setEmailStatus(prev => ({ ...prev, [cleanEmail]: status }));

    // Explicitly validate form to reflect new status
    setTimeout(() => formik.validateForm(), 100);
  };

  const handleManualEmailDelete = (tagToDelete) => {
    const currentEmails = formik.values.email.split(",").map(i => i.trim()).filter(Boolean);
    const newEmails = currentEmails.filter(email => email !== tagToDelete);
    formik.setFieldValue("email", newEmails.join(", "), true);
  };

  const handleCSVUpload = () => {
    if (csvFile) {
      handleClearCSV();
    } else {
      document.getElementById("csvFileInput")?.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== "text/csv" && !file.name.endsWith(".csv")) {
        toast.error("Please upload a valid CSV file.");
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target.result;
        try {
          const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
          const foundEmails = text.match(emailRegex);

          if (foundEmails && foundEmails.length > 0) {
            // Initial regex check and trimming
            const uniqueEmails = [...new Set(foundEmails.map(e => e.trim().toLowerCase()).filter(Boolean))];
            const validationMap = {};

            uniqueEmails.forEach(email => {
              const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
              validationMap[email] = isValid ? "pending" : "invalid";
            });

            formik.setFieldValue("email", uniqueEmails.join(", "), true);
            setCsvFile({
              name: file.name,
              count: uniqueEmails.length,
              emails: uniqueEmails,
              validation: validationMap
            });

            setIsVerifying(true);

            // Optimization: Fetch all merchants and invitations once to verify locally
            let allExistingMerchants = [];
            let allExistingInvites = [];

            try {
              const [mRes, iRes] = await Promise.all([
                axiosInstance.get("merchant?limit=1000"),
                axiosInstance.get("merchant/invitations?limit=1000")
              ]);
              allExistingMerchants = mRes?.data?.data || [];
              allExistingInvites = iRes?.data?.data || [];
            } catch (err) {
              console.error("Failed to fetch reference data for validation", err);
            }

            const emailsToVerify = uniqueEmails.filter(e => validationMap[e] === "pending");
            const newStatuses = {};

            emailsToVerify.forEach(email => {
              const cleanE = email.trim().toLowerCase();

              // 1. Check Merchant Collection (Local)
              const merchant = allExistingMerchants.find(m =>
                (m?.user?.email?.toLowerCase() === cleanE) ||
                (m?.email?.toLowerCase() === cleanE) ||
                (m?.businessEmail?.toLowerCase() === cleanE) ||
                (m?.business_email?.toLowerCase() === cleanE) ||
                (m?.merchantEmail?.toLowerCase() === cleanE)
              );

              if (merchant) {
                newStatuses[email] = merchant?.status?.toLowerCase() === "approved" ? "registered" : "in-process";
                return;
              }

              // 2. Check Invitation Collection (Local)
              const invitation = allExistingInvites.find(i => i?.email?.toLowerCase() === cleanE);
              if (invitation) {
                newStatuses[email] = "already_invited";
                return;
              }

              newStatuses[email] = "valid";
            });

            setEmailStatus(prev => ({ ...prev, ...newStatuses }));
            setCsvFile(prev => ({
              ...prev,
              validation: { ...prev.validation, ...newStatuses }
            }));

            // Explicitly validate form to reflect new status
            setTimeout(() => {
              formik.validateForm();
              formik.setFieldTouched("email", true, true);
            }, 200);

            const finalValidation = (await new Promise(resolve => {
              setCsvFile(prev => { resolve(prev?.validation); return prev; });
            }));

            const hasInvalid = Object.values(finalValidation || {}).some(v => v === "invalid" || v === "registered");
            if (hasInvalid) {
              formik.setFieldError("email", "One or more invalid or already registered emails detected.");
            } else {
              formik.setFieldError("email", "");
            }

          } else {
            formik.setFieldError("email", "No valid emails found in the CSV.");
          }
        } catch (error) {
          console.error("Error parsing CSV:", error);
          formik.setFieldError("email", "Failed to parse CSV file.");
        }
      };

      reader.onerror = () => {
        toast.error("Error reading file");
      };

      reader.readAsText(file);

      // Reset input value to allow uploading the same file again if needed
      event.target.value = "";
    }
  };

  const handleDeleteEmail = (index) => {
    const updatedEmails = [...csvFile.emails];
    updatedEmails.splice(index, 1);

    if (updatedEmails.length === 0) {
      handleClearCSV();
      setIsPreviewOpen(false);
    } else {
      const updatedValidation = { ...csvFile.validation };
      delete updatedValidation[csvFile.emails[index]];

      setCsvFile({
        ...csvFile,
        count: updatedEmails.length,
        emails: updatedEmails,
        validation: updatedValidation
      });
      formik.setFieldValue("email", updatedEmails.join(", "));

      // Check if errors still exist after deletion
      const hasInvalid = updatedEmails.some(e => updatedValidation[e] !== "valid");
      if (!hasInvalid) {
        formik.setFieldError("email", "");
      }
    }
  };

  const handleSaveEmail = async (index) => {
    if (!editedEmail.trim()) return;

    const updatedEmails = [...csvFile.emails];
    const oldEmail = updatedEmails[index];
    updatedEmails[index] = editedEmail;

    // Update state to pending while verifying
    const newValidation = { ...csvFile.validation, [editedEmail]: "pending" };
    setCsvFile(prev => ({ ...prev, emails: updatedEmails, validation: newValidation }));

    let status = "valid";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editedEmail)) {
      status = "invalid";
    } else {
      try {
        status = await checkMerchantRegistration(editedEmail);
        setEmailStatus(prev => ({ ...prev, [editedEmail]: status }));
      } catch (_) { }
    }

    setCsvFile(prev => {
      const updatedValidation = { ...prev.validation, [editedEmail]: status };
      // Remove old email if it was replaced
      if (oldEmail !== editedEmail) {
        delete updatedValidation[oldEmail];
      }

      const hasInvalid = updatedEmails.some(e =>
        updatedValidation[e] === "invalid" || updatedValidation[e] === "registered"
      );
      if (hasInvalid) {
        formik.setFieldError("email", "One or more invalid or already registered emails detected.");
      } else {
        formik.setFieldError("email", "");
      }
      return {
        ...prev,
        emails: updatedEmails,
        validation: updatedValidation
      };
    });

    formik.setFieldValue("email", updatedEmails.join(", "));
    setEditingRow(null);
    setEditedEmail("");
  };

  const getStatusBadge = (email) => {
    const status = csvFile?.validation?.[email];
    if (!status || status === "pending") return <span className="text-[#8C8C8C] animate-pulse">Checking...</span>;

    switch (status.toLowerCase()) {
      case "valid":
        return <span className="px-2 py-1 rounded-full bg-[#F0FDF4] text-[#15803D] text-[11px] font-[500] whitespace-nowrap">Valid</span>;
      case "invalid":
        return <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">Invalid Format</span>;
      case "in-process":
        return <span className="px-2 py-1 rounded-full bg-[#E0F2FE] text-[#0369A1] text-[11px] font-[500] whitespace-nowrap">In-process</span>;
      case "registered":
      case "already_merchant":
      case "already_registered":
        return <span className="px-2 py-1 rounded-full bg-[#FEF2F2] text-[#B91C1C] text-[11px] font-[500] whitespace-nowrap">Registered Merchant</span>;
      case "already_invited":
        return <span className="px-2 py-1 rounded-full bg-[#FFF7ED] text-[#C2410C] text-[11px] font-[500] whitespace-nowrap">Already Invited</span>;
      default:
        return <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-[11px] font-[500] whitespace-nowrap">{status.replace(/_/g, " ")}</span>;
    }
  };

  return (
    <>
      {loading === true && <Loader />}

      <div className="bg-white rounded-[10px] mt-0 pb-[80px] pt-12 shadow-[0px_4px_8px_0px_#0000001A] w-full min-h-[85vh] flex flex-col justify-center">
        <div className="flex flex-col items-center justify-center w-full px-4">

          <div className="w-full max-w-[600px]">
            <h1 className="text-[32px] font-[500] text-[#303030] mb-0 font-poppins text-left">
              Let’s Invite <span className="text-[#00ADEF]">Merchants!</span>
            </h1>

            <p className="text-[#767676] font-poppins text-[16px] font-[400] mb-10 text-left">
              Invite merchants by entering their email to join Swoodle
            </p>

            <div className="w-full mb-1">
              <label className="block text-[#303030] bg-white text-[14px] font-[600] font-poppins mb-2">
                Merchant Email(s)<span className="text-red-500">*</span>
              </label>
              <div className="w-full relative">
                {csvFile ? (
                  <div className="w-full h-[50px] border border-[#E0E0E0] rounded-[5px] bg-white flex items-center px-4 mb-0 transition-all duration-300">
                    <div className="flex items-center gap-2 bg-white border border-[#00ADEF]/20 text-[#00ADEF] px-4 py-1.5 rounded-full text-[14px] font-[500] font-poppins shadow-sm">
                      <CsvIcon className="w-4 h-4" />
                      <span className="truncate max-w-[350px]">{csvFile.name}</span>
                      <div
                        className="ml-1 p-0.5 hover:bg-[#00ADEF] hover:text-white rounded-full transition-colors cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClearCSV();
                        }}
                      >
                        <X size={12} />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={`w-full min-h-[50px] border ${touched.email && errors.email ? "border-[#DC2626]" : "border-[#E0E0E0]"} rounded-[5px] bg-white flex flex-wrap items-center px-3 py-1 gap-2 transition-all duration-300`}>
                    {email.split(",").map(i => i.trim()).filter(Boolean).map((tag, idx) => (
                      <div key={idx} className="flex items-center gap-2 bg-[#F0F9FF] border border-[#00ADEF]/20 text-[#00ADEF] px-3 py-1 rounded-full text-[13px] font-[500] font-poppins shadow-sm animate-in fade-in zoom-in duration-200">
                        <span>{tag}</span>
                        <div
                          className="ml-1 p-0.5 hover:bg-[#00ADEF] hover:text-white rounded-full transition-colors cursor-pointer"
                          onClick={() => handleManualEmailDelete(tag)}
                        >
                          <X size={10} />
                        </div>
                      </div>
                    ))}
                    <input
                      className="flex-1 outline-none border-none min-w-[150px] h-[38px] text-[14px] font-poppins text-[#303030] placeholder:text-[#8C8C8C]"
                      placeholder={email ? "" : "Enter merchant email(s)"}
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "," || e.key === "Enter") {
                          e.preventDefault();
                          handleManualEmailAdd(inputValue);
                        } else if (e.key === "Backspace" && !inputValue && email) {
                          const currentEmails = email.split(",").map(i => i.trim()).filter(Boolean);
                          if (currentEmails.length > 0) {
                            handleManualEmailDelete(currentEmails[currentEmails.length - 1]);
                          }
                        }
                      }}
                      onBlur={() => {
                        if (inputValue) handleManualEmailAdd(inputValue);
                        // Only set touched if we have no tags and input is empty to avoid "Email is required" jumpiness
                        if (!email.trim() && !inputValue) {
                          formik.setFieldTouched("email", true, true);
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              {touched.email && errors.email && !csvFile && (
                <p className="text-[#DC2626] text-[12px] font-poppins font-[400] mt-1">{errors.email}</p>
              )}

              <div className="flex items-center mt-2 gap-2">
                <Tooltip title="Invite multiple merchants by separating their email addresses with commas." arrow placement="right">
                  <span className="cursor-pointer">
                    <InfoIcon className="w-5 h-5 text-[#00ADEF]" />
                  </span>
                </Tooltip>
                <p className="text-[#8C8C8C] text-[12px] font-poppins font-[400]">
                  Invite multiple merchants by separating their email addresses with commas.
                </p>
                {csvFile && (
                  <div
                    className="ml-auto flex items-center gap-1 cursor-pointer text-[#00ADEF] hover:text-[#0088CC] transition-colors"
                    onClick={() => setIsPreviewOpen(true)}
                  >
                    <Eye size={16} />
                    <span className="text-[12px] font-[500] font-poppins underline">Preview File</span>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-center w-full mb-3 relative">
              <div className="h-[1px] bg-[#E0E0E0] w-full absolute z-0"></div>
              <div className="bg-white px-4 z-10 text-[#303030] text-[14px] font-poppins font-[400]">Or</div>
            </div>

            <div className="flex gap-4 mb-8 sm:flex-col md:flex-row">
              <button
                onClick={handleCSVUpload}
                disabled={!!csvFile}
                className={`flex-1 flex items-center justify-center gap-3 border rounded-[5px] py-4 transition-colors h-[50px] ${csvFile ? "border-[#E0E0E0] bg-[#F5F5F5] cursor-not-allowed" : "border-[#E0E0E0] bg-white hover:bg-gray-50"
                  }`}
              >
                <UploadIcon className="w-5 h-5 text-[#00ADEF]" />
                <span className={`font-poppins font-[500] text-[14px] ${csvFile ? "text-[#8C8C8C]" : "text-[#303030]"}`}>
                  {csvFile ? "CSV File Uploaded" : "Upload CSV File"}
                </span>
                <input type="file" id="csvFileInput" hidden accept=".csv" onChange={handleFileChange} />
              </button>

              <button
                onClick={handleSampleDownload}
                className="flex-1 flex items-center justify-center gap-3 border border-[#E0E0E0] rounded-[5px] py-4 bg-white hover:bg-gray-50 transition-colors h-[50px]"
              >
                <CsvIcon className="w-6 h-6" />
                <span className="text-[#303030] font-poppins font-[500] text-[14px]">Sample File</span>
                <DownloadIcon className="w-5 h-5 text-[#00ADEF]" />
              </button>
            </div>

            {csvFile && Object.values(csvFile.validation || {}).some(v => v === "invalid" || v === "registered") && (
              <div className="mb-4 p-3 bg-[#FFF7ED] border border-[#FED7AA] rounded-[5px] flex items-center gap-2">
                <InfoIcon className="w-5 h-5 text-[#EA580C]" />
                <p className="text-[12px] text-[#9A3412] font-poppins">
                  {(() => {
                    const validationValues = Object.values(csvFile.validation || {});
                    const registeredCount = validationValues.filter(v => v === "registered").length;
                    const invalidCount = validationValues.filter(v => v === "invalid").length;

                    if (registeredCount > 0 && invalidCount > 0) {
                      return "Some emails are already Registered Merchants and some have an invalid format. Please check the preview.";
                    }
                    if (registeredCount > 0) {
                      return "Some emails in your list are already Registered Merchants. Please check the preview.";
                    }
                    return "Some emails in your list have an invalid format. Please check the preview file for details.";
                  })()}
                </p>
              </div>
            )}

            <div className="w-full">
              <button
                type="submit"
                disabled={loading || !formik.isValid || !formik.values.email}
                className="w-full bg-[#00ADEF] text-white rounded-[5px] py-3 text-[16px] font-[500] font-poppins enabled:hover:bg-[#0088CC] transition-colors disabled:opacity-50 disabled:cursor-not-allowed h-[50px]"
                onClick={() => {
                  formik.handleSubmit();
                }}
              >
                {loading ? "Sending..." : "Send Invitation"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isPreviewOpen && (
        <DetailsModal
          handleClose={() => setIsPreviewOpen(false)}
          popupClassName="!w-[700px]"
          content={
            <div className="w-full flex flex-col items-start gap-4 p-2">
              <h3 className="text-[20px] font-[600] text-[#00ADEF] font-poppins">
                {csvFile?.name}
              </h3>
              <div className="relative w-full max-h-[400px] overflow-y-auto border border-[#E0E0E0] rounded-[8px] p-0">
                <table className="w-full text-left border-collapse">
                  <thead className="bg-[#F5F5F5] sticky top-0 z-20">
                    <tr>
                      <th className="p-3 text-[14px] font-[600] text-[#303030] font-poppins border-b border-[#E0E0E0] bg-[#F5F5F5]">#</th>
                      <th className="p-3 text-[14px] font-[600] text-[#303030] font-poppins border-b border-[#E0E0E0] bg-[#F5F5F5]">Email Address</th>
                      <th className="p-3 text-[14px] font-[600] text-[#303030] font-poppins border-b border-[#E0E0E0] bg-[#F5F5F5] w-[150px]">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvFile?.emails?.map((email, index) => (
                      <tr key={index} className="hover:bg-[#F9F9F9] border-b border-[#E0E0E0] last:border-0 group">
                        <td className="p-3 text-[14px] font-[400] text-[#767676] font-poppins border-r border-[#E0E0E0] w-[50px]">{index + 1}</td>
                        <td className="p-3 text-[14px] font-[400] text-[#767676] font-poppins relative border-r border-[#E0E0E0]">
                          {editingRow === index ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editedEmail}
                                onChange={(e) => setEditedEmail(e.target.value)}
                                className="border border-[#00ADEF] rounded px-2 py-1 text-[14px] w-full outline-none"
                                autoFocus
                              />
                              <button onClick={() => handleSaveEmail(index)} className="p-1 hover:bg-green-50 rounded text-green-600">
                                <Check size={16} />
                              </button>
                              <button onClick={() => setEditingRow(null)} className="p-1 hover:bg-red-50 rounded text-red-600">
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <span
                                className="cursor-pointer hover:text-[#00ADEF]"
                                onClick={() => {
                                  setEditingRow(index);
                                  setEditedEmail(email);
                                }}
                              >
                                {email}
                              </span>
                              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => {
                                    setEditingRow(index);
                                    setEditedEmail(email);
                                  }}
                                  className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-[#00ADEF]"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={() => handleDeleteEmail(index)}
                                  className="p-1 hover:bg-red-50 rounded text-gray-500 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="p-3 text-[14px] font-[400] font-poppins">
                          {getStatusBadge(email)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-[12px] text-[#8C8C8C] font-poppins">
                Total count: {csvFile?.count}
              </div>
            </div>
          }
        />
      )}
    </>
  );
};

export default InvitaionFormPage;
